import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Clock, Star, Navigation, 
  Heart, Share2, ChevronLeft, Bed, Stethoscope,
  Shield, Calendar, Users, Building2, AlertCircle,
  CheckCircle, Package, Activity
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import apiService from '../../services/api';

const HospitalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hospital, setHospital] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [error, setError] = useState('');
  const [doctorsError, setDoctorsError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    fetchHospitalDetails();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'doctors' && hospital && doctors.length === 0 && !doctorsError) {
      fetchDoctors();
    }
  }, [activeTab, hospital]);

  const fetchHospitalDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.hospitals.getById(id);
      
      if (response.success) {
        setHospital(response.data);
      } else {
        setError(response.message || 'Failed to load hospital details');
      }
    } catch (err) {
      console.error('Error fetching hospital:', err);
      setError('Failed to load hospital details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      setLoadingDoctors(true);
      setDoctorsError('');
        const response = await apiService.hospitals.getDoctors(id);
      
      
      if (response.success) {
        // Handle different response structures
        let doctorsList = [];
        
        if (Array.isArray(response.data)) {
          // Direct array
          doctorsList = response.data;
        } else if (response.data && typeof response.data === 'object') {
          // Check common nested structures
          if (Array.isArray(response.data.doctors)) {
            doctorsList = response.data.doctors;
          } else if (Array.isArray(response.data.data)) {
            doctorsList = response.data.data;
          } else if (Array.isArray(response.data.results)) {
            doctorsList = response.data.results;
          } else {
            
            doctorsList = [];
          }
        }
        
       
        
        setDoctors(doctorsList);
        
        if (doctorsList.length === 0) {
          setDoctorsError('No doctors are currently registered at this hospital');
        }
      } else {
        setDoctorsError(response.message || 'Failed to load doctors');
      }
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setDoctorsError(err.message || 'Failed to load doctors. Please try again.');
    } finally {
      setLoadingDoctors(false);
    }
  };

  const handleCall = () => {
    const phone = hospital?.contact?.phone || hospital?.contact?.emergency;
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleEmail = () => {
    if (hospital?.contact?.email) {
      window.location.href = `mailto:${hospital.contact.email}`;
    }
  };

  const handleDirections = () => {
    if (hospital?.location?.coordinates) {
      const [lng, lat] = hospital.location.coordinates;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: hospital?.name,
          text: `Check out ${hospital?.name} on MediGo`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const getOperatingStatus = () => {
    if (!hospital?.operatingHours) {
      return { text: 'Hours not available', color: 'gray' };
    }
    
    if (hospital.operatingHours.is24Hours) {
      return { text: '24/7 Open', color: 'green' };
    }
    
    return hospital.currentStatus || { text: 'Check hours', color: 'gray' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner size="lg" text="Loading hospital details..." />
      </div>
    );
  }

  if (error || !hospital) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Hospital Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The hospital you\'re looking for doesn\'t exist.'}</p>
          <button
            onClick={() => navigate('/hospitals')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Hospitals
          </button>
        </div>
      </div>
    );
  }

  const primaryImage = hospital.images?.find(img => img.isPrimary)?.url || hospital.images?.[0]?.url;
  const operatingStatus = getOperatingStatus();
  const doctorCount = Array.isArray(hospital.doctors) ? hospital.doctors.length : 0;
  const ambulanceCount = Array.isArray(hospital.ambulances) ? hospital.ambulances.length : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ChevronLeft size={20} />
            <span className="font-medium">Back</span>
          </button>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex-1 flex items-start gap-4">
              {primaryImage ? (
                <img
                  src={primaryImage}
                  alt={hospital.name}
                  className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-24 h-24 rounded-lg bg-primary-100 flex items-center justify-center border-2 border-primary-200">
                  <Building2 className="w-12 h-12 text-primary-600" />
                </div>
              )}

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{hospital.name}</h1>
                
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {hospital.rating?.average > 0 && (
                    <div className="flex items-center gap-1">
                      <Star size={16} className="text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold text-gray-900">{hospital.rating.average.toFixed(1)}</span>
                      <span className="text-gray-600 text-sm">({hospital.rating.totalReviews || 0})</span>
                    </div>
                  )}
                  
                  {hospital.isVerified && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      <CheckCircle size={14} />
                      <span>Verified</span>
                    </div>
                  )}
                  
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                    operatingStatus.color === 'green' ? 'bg-green-100 text-green-700' :
                    operatingStatus.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    <Clock size={14} />
                    <span>{operatingStatus.text}</span>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-gray-600">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{hospital.location?.address || 'Address not available'}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCall}
                disabled={!hospital.contact?.phone && !hospital.contact?.emergency}
                className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Phone size={18} />
                <span className="hidden sm:inline">Call Now</span>
              </button>
              <button
                onClick={toggleFavorite}
                className={`p-3 border rounded-lg transition-colors ${
                  isFavorite 
                    ? 'bg-red-50 border-red-200 text-red-600' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-600'
                }`}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={handleShare}
                className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                title="Share hospital"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              onClick={handleDirections}
              disabled={!hospital.location?.coordinates}
              className="flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Navigation size={18} className="text-primary-600" />
              <span className="text-sm font-medium">Directions</span>
            </button>
            <button
              onClick={handleCall}
              disabled={!hospital.contact?.phone && !hospital.contact?.emergency}
              className="flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Phone size={18} className="text-primary-600" />
              <span className="text-sm font-medium">Call</span>
            </button>
            <button
              onClick={handleEmail}
              disabled={!hospital.contact?.email}
              className="flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail size={18} className="text-primary-600" />
              <span className="text-sm font-medium">Email</span>
            </button>
            <button
              onClick={() => navigate(`/hospitals/${id}/book`)}
              className="flex items-center justify-center gap-2 p-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Calendar size={18} />
              <span className="text-sm font-medium">Book</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 overflow-x-auto">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'services', label: 'Services' },
              { key: 'doctors', label: `Doctors ${doctors.length > 0 ? `(${doctors.length})` : ''}` },
              { key: 'facilities', label: 'Facilities' },
              { key: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">About</h2>
                  <p className="text-gray-600 leading-relaxed">
                    {hospital.description || 'No description available for this hospital.'}
                  </p>
                </div>

                {hospital.images && hospital.images.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Photos</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {hospital.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image.url}
                            alt={image.caption || `Hospital photo ${index + 1}`}
                            className="w-full h-40 object-cover rounded-lg cursor-pointer hover:opacity-90 transition"
                            onClick={() => window.open(image.url, '_blank')}
                          />
                          {image.isPrimary && (
                            <div className="absolute top-2 left-2 bg-primary-500 text-white px-2 py-0.5 rounded text-xs font-semibold">
                              Primary
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Key Statistics</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {hospital.capacity?.totalBeds > 0 && (
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <Bed className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{hospital.capacity.totalBeds}</p>
                        <p className="text-sm text-gray-600">Total Beds</p>
                      </div>
                    )}
                    {hospital.capacity?.icuBeds > 0 && (
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <Activity className="w-8 h-8 text-red-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{hospital.capacity.icuBeds}</p>
                        <p className="text-sm text-gray-600">ICU Beds</p>
                      </div>
                    )}
                    {doctorCount > 0 && (
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <Stethoscope className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{doctorCount}</p>
                        <p className="text-sm text-gray-600">Doctors</p>
                      </div>
                    )}
                    {ambulanceCount > 0 && (
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <Package className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{ambulanceCount}</p>
                        <p className="text-sm text-gray-600">Ambulances</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Medical Services</h2>
                {hospital.services && hospital.services.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {hospital.services.map((service, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">{service.name}</p>
                          {service.description && (
                            <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No services listed</p>
                  </div>
                )}
              </div>
            )}

            {/* Doctors Tab */}
            {activeTab === 'doctors' && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Our Doctors</h2>
                  {doctors.length > 0 && (
                    <button
                      onClick={fetchDoctors}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      Refresh
                    </button>
                  )}
                </div>
                
                {loadingDoctors ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner text="Loading doctors..." />
                  </div>
                ) : doctorsError ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">{doctorsError}</p>
                    <button
                      onClick={fetchDoctors}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                ) : doctors && doctors.length > 0 ? (
                  <div className="space-y-4">
                    {doctors.map((doctor) => (
                      <div key={doctor._id} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 transition">
                        {doctor.profileImage ? (
                          <img
                            src={doctor.profileImage}
                            alt={`Dr. ${doctor.firstName} ${doctor.lastName}`}
                            className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <Stethoscope className="w-8 h-8 text-primary-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">
                            Dr. {doctor.firstName} {doctor.lastName}
                          </h3>
                          <p className="text-sm text-gray-600">{doctor.specialization}</p>
                          {doctor.experience && (
                            <p className="text-sm text-gray-500 mt-1">{doctor.experience} years experience</p>
                          )}
                          {doctor.qualifications && doctor.qualifications.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              {doctor.qualifications.join(', ')}
                            </p>
                          )}
                          {doctor.rating?.average > 0 && (
                            <div className="flex items-center gap-1 mt-2">
                              <Star size={14} className="text-yellow-500 fill-yellow-500" />
                              <span className="text-sm font-medium">{doctor.rating.average.toFixed(1)}</span>
                              <span className="text-xs text-gray-500">({doctor.rating.totalReviews || 0} reviews)</span>
                            </div>
                          )}
                        </div>
                        {doctor.consultationFee && (
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Consultation</p>
                            <p className="font-semibold text-gray-900">
                              KES {typeof doctor.consultationFee === 'object' 
                                ? (doctor.consultationFee.physical || doctor.consultationFee.online || 0)
                                : doctor.consultationFee}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No doctors are currently listed for this hospital</p>
                  </div>
                )}
              </div>
            )}

            {/* Facilities Tab */}
            {activeTab === 'facilities' && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Facilities & Amenities</h2>
                {hospital.facilities && hospital.facilities.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {hospital.facilities.map((facility, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-gray-700">{facility.name || facility}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No facilities information available</p>
                  </div>
                )}

                {hospital.departments && hospital.departments.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Departments</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {hospital.departments.map((dept, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <span className="text-gray-700">{dept.name || dept}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      <MapPin className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Address</p>
                      <p className="text-gray-600">{hospital.location?.address || 'Address not available'}</p>
                    </div>
                  </div>

                  {hospital.contact?.phone && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Phone className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Phone</p>
                        <a 
                          href={`tel:${hospital.contact.phone}`} 
                          className="text-primary-600 hover:underline font-medium"
                        >
                          {hospital.contact.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {hospital.contact?.emergency && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-red-100 rounded-lg">
                        <Phone className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Emergency</p>
                        <a 
                          href={`tel:${hospital.contact.emergency}`} 
                          className="text-red-600 hover:underline font-medium"
                        >
                          {hospital.contact.emergency}
                        </a>
                      </div>
                    </div>
                  )}

                  {hospital.contact?.email && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Mail className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Email</p>
                        <a 
                          href={`mailto:${hospital.contact.email}`} 
                          className="text-primary-600 hover:underline"
                        >
                          {hospital.contact.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {hospital.contact?.website && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <Navigation className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Website</p>
                        <a 
                          href={hospital.contact.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          {hospital.contact.website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary-600" />
                Operating Hours
              </h3>
              <div className="space-y-2 text-sm">
                {hospital.operatingHours ? (
                  hospital.operatingHours.is24Hours ? (
                    <div className="text-center py-4 bg-green-50 rounded-lg">
                      <p className="font-semibold text-green-700">Open 24/7</p>
                    </div>
                  ) : (
                    Object.entries(hospital.operatingHours).map(([day, hours]) => {
                      if (day === 'is24Hours' || typeof hours !== 'object') return null;
                      
                      return (
                        <div key={day} className="flex justify-between py-1">
                          <span className="text-gray-600 capitalize">{day}</span>
                          <span className="text-gray-900 font-medium">
                            {hours.isClosed ? (
                              <span className="text-red-600">Closed</span>
                            ) : (
                              `${hours.open} - ${hours.close}`
                            )}
                          </span>
                        </div>
                      );
                    })
                  )
                ) : (
                  <p className="text-gray-500 text-center py-4">Hours not available</p>
                )}
              </div>
            </div>

            {hospital.hasEmergency && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="text-red-600" size={20} />
                  <h3 className="font-bold text-red-900">Emergency Services</h3>
                </div>
                <p className="text-sm text-red-700">24/7 emergency care available</p>
                {hospital.contact?.emergency && (
                  <button
                    onClick={() => window.location.href = `tel:${hospital.contact.emergency}`}
                    className="w-full mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    Call Emergency
                  </button>
                )}
              </div>
            )}

            {hospital.insurance && hospital.insurance.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary-600" />
                  Insurance Accepted
                </h3>
                <div className="space-y-2">
                  {hospital.insurance.map((ins, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle size={16} className="text-green-600" />
                      <span className="text-gray-700">{ins.provider || ins}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg shadow-sm p-6 text-white">
              <h3 className="font-bold mb-4">Quick Info</h3>
              <div className="space-y-3 text-sm">
                {hospital.capacity?.totalBeds > 0 && (
                  <div className="flex justify-between">
                    <span className="text-primary-100">Total Beds</span>
                    <span className="font-semibold">{hospital.capacity.totalBeds}</span>
                  </div>
                )}
                {hospital.services && hospital.services.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-primary-100">Services</span>
                    <span className="font-semibold">{hospital.services.length}</span>
                  </div>
                )}
                {doctorCount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-primary-100">Doctors</span>
                    <span className="font-semibold">{doctorCount}</span>
                  </div>
                )}
                {doctors.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-primary-100">Available Doctors</span>
                    <span className="font-semibold">{doctors.length}</span>
                  </div>
                )}
                {hospital.departments && hospital.departments.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-primary-100">Departments</span>
                    <span className="font-semibold">{hospital.departments.length}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalDetail;